import { useEffect, useRef, useState, useCallback } from "react";

const F = {
  X: 0,
  Y: 1,
  Z: 2,
  X_VEL: 3,
  Y_VEL: 4,
  X_ACC: 5,
  Y_ACC: 6,
  WIDTH: 7,
  HEIGHT: 8,
  WIDTH_VEL: 9,
  WIDTH_ACC: 10,
  HEIGHT_VEL: 11,
  HEIGHT_ACC: 12,
  ROTATE: 13,
  ROT_VEL: 14,
  ROT_ACC: 15,
  OPACITY: 16,
  ORIGIN_X: 17,
  ORIGIN_Y: 18,
  FONT_SIZE: 19,
  FONT_WEIGHT: 20,
  LINE_HEIGHT: 21,
  LETTER_SPACING: 22,
  SHADOW_BLUR: 23,
  SHADOW_OFFSET_X: 24,
  SHADOW_OFFSET_Y: 25,
  RADIUS_TL: 26,
  RADIUS_TR: 27,
  RADIUS_BL: 28,
  RADIUS_BR: 29,
  BORDER_TOP_W: 30,
  BORDER_RIGHT_W: 31,
  BORDER_BOTTOM_W: 32,
  BORDER_LEFT_W: 33,
};

const FIELDS_PER_COMPONENT = 34;
const SNAPSHOT_INTERVAL = 5;
const FIXED_DT = 1 / 120;

const FIELD_NAME_TO_IDX = {
  x: F.X,
  y: F.Y,
  z: F.Z,
  xVelocity: F.X_VEL,
  yVelocity: F.Y_VEL,
  xAcceleration: F.X_ACC,
  yAcceleration: F.Y_ACC,
  width: F.WIDTH,
  height: F.HEIGHT,
  widthVelocity: F.WIDTH_VEL,
  widthAcceleration: F.WIDTH_ACC,
  heightVelocity: F.HEIGHT_VEL,
  heightAcceleration: F.HEIGHT_ACC,
  rotate: F.ROTATE,
  rotationVelocity: F.ROT_VEL,
  rotationAcceleration: F.ROT_ACC,
  opacity: F.OPACITY,
  originX: F.ORIGIN_X,
  originY: F.ORIGIN_Y,
  fontSize: F.FONT_SIZE,
  fontWeight: F.FONT_WEIGHT,
  lineHeight: F.LINE_HEIGHT,
  letterSpacing: F.LETTER_SPACING,
  shadowBlur: F.SHADOW_BLUR,
  shadowOffsetX: F.SHADOW_OFFSET_X,
  shadowOffsetY: F.SHADOW_OFFSET_Y,
  radiusTopLeft: F.RADIUS_TL,
  radiusTopRight: F.RADIUS_TR,
  radiusBottomLeft: F.RADIUS_BL,
  radiusBottomRight: F.RADIUS_BR,
  borderTopWidth: F.BORDER_TOP_W,
  borderRightWidth: F.BORDER_RIGHT_W,
  borderBottomWidth: F.BORDER_BOTTOM_W,
  borderLeftWidth: F.BORDER_LEFT_W,
};

const STRING_FIELDS = [
  "color",
  "backgroundColor",
  "shadowColor",
  "text",
  "textAlign",
  "verticalAlign",
  "overflow",
  "shape",
  "borderTopType",
  "borderTopColor",
  "borderRightType",
  "borderRightColor",
  "borderBottomType",
  "borderBottomColor",
  "borderLeftType",
  "borderLeftColor",
];

const BOOLEAN_FIELDS = ["visibility"];

const NUMERIC_DEFAULTS = new Float64Array([
  0, 0, 0,
  0, 0, 0, 0,
  25, 25,
  0, 0, 0, 0,
  0, 0, 0,
  1,
  50, 50,
  16, 400,
  1.2, 0,
  0, 0, 0,
  0, 0, 0, 0,
  0, 0, 0, 0,
]);

const DEFAULT_CAMERA = {
  x: 800,
  y: 450,
  z: 0,
  zoom: 1,
  rotation: 0,
  originX: 800,
  originY: 450,
  xVelocity: 0,
  yVelocity: 0,
  zVelocity: 0,
  xAcceleration: 0,
  yAcceleration: 0,
  zAcceleration: 0,
  zoomVelocity: 0,
  zoomAcceleration: 0,
  rotationVelocity: 0,
  rotationAcceleration: 0,
};

const getStringDefault = (field) => {
  const defaults = {
    color: "#ffffff",
    backgroundColor: "#000000",
    shadowColor: "#00000000",
    text: "",
    textAlign: "center",
    verticalAlign: "center",
    overflow: "hidden",
    shape: "rect",
    borderTopType: "solid",
    borderTopColor: "#000000",
    borderRightType: "solid",
    borderRightColor: "#000000",
    borderBottomType: "solid",
    borderBottomColor: "#000000",
    borderLeftType: "solid",
    borderLeftColor: "#000000",
  };
  return defaults[field] ?? "";
};

const separateTimelines = (timelines, componentIdToIndex) => {
  const numeric = [];
  const string = [];

  for (const tl of timelines) {
    const compIdx = componentIdToIndex.get(
      tl.animationComponent?.animationComponentId,
    );
    if (compIdx === undefined) continue;

    const fieldIdx = FIELD_NAME_TO_IDX[tl.fieldName];
    if (fieldIdx !== undefined) {
      numeric.push({
        time: tl.time,
        compIdx,
        fieldIdx,
        value: Number(tl.value),
      });
    } else if (BOOLEAN_FIELDS.includes(tl.fieldName)) {
      string.push({
        time: tl.time,
        compIdx,
        fieldName: tl.fieldName,
        value: Boolean(tl.value),
      });
    } else if (STRING_FIELDS.includes(tl.fieldName)) {
      string.push({
        time: tl.time,
        compIdx,
        fieldName: tl.fieldName,
        value: String(tl.value),
      });
    }
  }

  numeric.sort((a, b) => a.time - b.time);
  string.sort((a, b) => a.time - b.time);
  return { numeric, string };
};

const buildBuffer = (components) => {
  const n = components.length;
  const buf = new Float64Array(n * FIELDS_PER_COMPONENT);
  for (let i = 0; i < n; i++) {
    const off = i * FIELDS_PER_COMPONENT;
    const c = components[i];
    buf.set(NUMERIC_DEFAULTS, off);
    for (const [name, idx] of Object.entries(FIELD_NAME_TO_IDX)) {
      if (c[name] !== undefined && c[name] !== null) {
        buf[off + idx] = Number(c[name]);
      }
    }
  }
  return buf;
};

const buildStringState = (components) =>
  components.map((c) => {
    const state = {};
    for (const field of STRING_FIELDS) {
      state[field] = c[field] !== undefined ? c[field] : getStringDefault(field);
    }
    state.visibility = c.visibility !== undefined ? c.visibility : false;
    state.animationComponentId = c.animationComponentId;
    state.animationComponentName = c.animationComponentName;
    return state;
  });

const stepCamera = (cam, dt) => {
  const cvx = cam.xVelocity;
  const cax = cam.xAcceleration;
  const cvy = cam.yVelocity;
  const cay = cam.yAcceleration;
  cam.x += cvx * dt + 0.5 * cax * dt * dt;
  cam.y += cvy * dt + 0.5 * cay * dt * dt;
  cam.xVelocity = cvx + cax * dt;
  cam.yVelocity = cvy + cay * dt;

  const cvz = cam.zVelocity;
  const caz = cam.zAcceleration;
  cam.z += cvz * dt + 0.5 * caz * dt * dt;
  cam.zVelocity = cvz + caz * dt;

  const cvzoom = cam.zoomVelocity;
  const cazoom = cam.zoomAcceleration;
  cam.zoom += cvzoom * dt + 0.5 * cazoom * dt * dt;
  cam.zoomVelocity = cvzoom + cazoom * dt;

  const cvr = cam.rotationVelocity;
  const car = cam.rotationAcceleration;
  cam.rotation += cvr * dt + 0.5 * car * dt * dt;
  cam.rotationVelocity = cvr + car * dt;
};

const useAnimationEngine = (
  initialComponents = [],
  timelines = [],
  initialCamera = {},
  cameraTimelines = [],
  duration = 0,
) => {
  const bufRef = useRef(null);
  const initBufRef = useRef(null);
  const strStateRef = useRef([]);
  const initStrStateRef = useRef([]);
  const nRef = useRef(0);

  const numTlRef = useRef([]);
  const strTlRef = useRef([]);
  const camTlRef = useRef([]);

  const numTlPtrRef = useRef(0);
  const strTlPtrRef = useRef(0);
  const camTlPtrRef = useRef(0);

  const cameraRef = useRef({ ...DEFAULT_CAMERA });
  const initCameraRef = useRef({ ...DEFAULT_CAMERA });
  const currentTimeRef = useRef(0);
  const accumulatorRef = useRef(0);
  const lastTimeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const speedRef = useRef(1);
  const rafRef = useRef(null);

  const snapshotsRef = useRef([]);
  const snapTimerRef = useRef(null);

  const [, forceRender] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const applyNumericTimelines = (time) => {
    const tls = numTlRef.current;
    const buf = bufRef.current;
    let ptr = numTlPtrRef.current;
    while (ptr < tls.length && tls[ptr].time <= time) {
      const tl = tls[ptr];
      buf[tl.compIdx * FIELDS_PER_COMPONENT + tl.fieldIdx] = tl.value;
      ptr++;
    }
    numTlPtrRef.current = ptr;
  };

  const applyStringTimelines = (time) => {
    const tls = strTlRef.current;
    const str = strStateRef.current;
    let ptr = strTlPtrRef.current;
    while (ptr < tls.length && tls[ptr].time <= time) {
      const tl = tls[ptr];
      str[tl.compIdx][tl.fieldName] = tl.value;
      ptr++;
    }
    strTlPtrRef.current = ptr;
  };

  const applyCameraTimelines = (time) => {
    const tls = camTlRef.current;
    let ptr = camTlPtrRef.current;
    while (ptr < tls.length && tls[ptr].time <= time) {
      cameraRef.current[tls[ptr].fieldName] = tls[ptr].value;
      ptr++;
    }
    camTlPtrRef.current = ptr;
  };

  const initEngine = useCallback((comps, tls, initCam, camTls) => {
    nRef.current = comps.length;

    const idToIndex = new Map();
    comps.forEach((component, index) =>
      idToIndex.set(component.animationComponentId, index),
    );

    const buf = buildBuffer(comps);
    bufRef.current = buf;
    initBufRef.current = new Float64Array(buf);

    const strState = buildStringState(comps);
    strStateRef.current = strState;
    initStrStateRef.current = strState.map((entry) => ({ ...entry }));

    const { numeric, string } = separateTimelines(tls, idToIndex);
    numTlRef.current = numeric;
    strTlRef.current = string;
    camTlRef.current = (camTls || []).slice().sort((a, b) => a.time - b.time);

    cameraRef.current = { ...DEFAULT_CAMERA, ...initCam };
    initCameraRef.current = { ...DEFAULT_CAMERA, ...initCam };

    numTlPtrRef.current = 0;
    strTlPtrRef.current = 0;
    camTlPtrRef.current = 0;
    currentTimeRef.current = 0;
    accumulatorRef.current = 0;
    lastTimeRef.current = null;
    snapshotsRef.current = [];

    applyNumericTimelines(0);
    applyStringTimelines(0);
    applyCameraTimelines(0);
    forceRender((value) => value + 1);
  }, []);

  const resetState = useCallback(() => {
    bufRef.current.set(initBufRef.current);
    const init = initStrStateRef.current;
    const str = strStateRef.current;
    for (let i = 0; i < init.length; i++) {
      Object.assign(str[i], init[i]);
    }
    cameraRef.current = { ...initCameraRef.current };
    numTlPtrRef.current = 0;
    strTlPtrRef.current = 0;
    camTlPtrRef.current = 0;
    currentTimeRef.current = 0;
    accumulatorRef.current = 0;
    lastTimeRef.current = null;
  }, []);

  const step = (dt) => {
    const buf = bufRef.current;
    for (let i = 0; i < nRef.current; i++) {
      const off = i * FIELDS_PER_COMPONENT;
      const xv = buf[off + F.X_VEL];
      const xa = buf[off + F.X_ACC];
      const yv = buf[off + F.Y_VEL];
      const ya = buf[off + F.Y_ACC];
      const rv = buf[off + F.ROT_VEL];
      const ra = buf[off + F.ROT_ACC];
      const wv = buf[off + F.WIDTH_VEL];
      const wa = buf[off + F.WIDTH_ACC];
      const hv = buf[off + F.HEIGHT_VEL];
      const ha = buf[off + F.HEIGHT_ACC];
      const dt2 = 0.5 * dt * dt;

      if (xv !== 0 || xa !== 0) {
        buf[off + F.X] += xv * dt + xa * dt2;
        buf[off + F.X_VEL] = xv + xa * dt;
      }
      if (yv !== 0 || ya !== 0) {
        buf[off + F.Y] += yv * dt + ya * dt2;
        buf[off + F.Y_VEL] = yv + ya * dt;
      }
      if (rv !== 0 || ra !== 0) {
        buf[off + F.ROTATE] += rv * dt + ra * dt2;
        buf[off + F.ROT_VEL] = rv + ra * dt;
      }
      if (wv !== 0 || wa !== 0) {
        buf[off + F.WIDTH] += wv * dt + wa * dt2;
        buf[off + F.WIDTH_VEL] = wv + wa * dt;
      }
      if (hv !== 0 || ha !== 0) {
        buf[off + F.HEIGHT] += hv * dt + ha * dt2;
        buf[off + F.HEIGHT_VEL] = hv + ha * dt;
      }
    }

    stepCamera(cameraRef.current, dt);
  };

  const buildSnapshots = useCallback(() => {
    if (!duration) return;
    if (snapTimerRef.current) clearTimeout(snapTimerRef.current);

    const snapBuf = new Float64Array(initBufRef.current);
    const snapStr = initStrStateRef.current.map((entry) => ({ ...entry }));
    const snapCamera = { ...initCameraRef.current };
    const snapNumTls = numTlRef.current;
    const snapStrTls = strTlRef.current;
    const snapCamTls = camTlRef.current;

    let snapTime = 0;
    let snapNumPtr = 0;
    let snapStrPtr = 0;
    let snapCamPtr = 0;
    const snapshots = [];

    const applySnap = (time) => {
      while (snapNumPtr < snapNumTls.length && snapNumTls[snapNumPtr].time <= time) {
        const tl = snapNumTls[snapNumPtr];
        snapBuf[tl.compIdx * FIELDS_PER_COMPONENT + tl.fieldIdx] = tl.value;
        snapNumPtr++;
      }
      while (snapStrPtr < snapStrTls.length && snapStrTls[snapStrPtr].time <= time) {
        const tl = snapStrTls[snapStrPtr];
        snapStr[tl.compIdx][tl.fieldName] = tl.value;
        snapStrPtr++;
      }
      while (snapCamPtr < snapCamTls.length && snapCamTls[snapCamPtr].time <= time) {
        snapCamera[snapCamTls[snapCamPtr].fieldName] = snapCamTls[snapCamPtr].value;
        snapCamPtr++;
      }
    };

    applySnap(0);
    snapshots.push({
      time: 0,
      buf: new Float64Array(snapBuf),
      str: snapStr.map((entry) => ({ ...entry })),
      camera: { ...snapCamera },
      numPtr: snapNumPtr,
      strPtr: snapStrPtr,
      camPtr: snapCamPtr,
    });

    const runChunk = () => {
      const start = performance.now();

      while (snapTime < duration) {
        for (let i = 0; i < nRef.current; i++) {
          const off = i * FIELDS_PER_COMPONENT;
          const xv = snapBuf[off + F.X_VEL];
          const xa = snapBuf[off + F.X_ACC];
          const yv = snapBuf[off + F.Y_VEL];
          const ya = snapBuf[off + F.Y_ACC];
          const rv = snapBuf[off + F.ROT_VEL];
          const ra = snapBuf[off + F.ROT_ACC];
          const wv = snapBuf[off + F.WIDTH_VEL];
          const wa = snapBuf[off + F.WIDTH_ACC];
          const hv = snapBuf[off + F.HEIGHT_VEL];
          const ha = snapBuf[off + F.HEIGHT_ACC];
          const dt2 = 0.5 * FIXED_DT * FIXED_DT;

          if (xv !== 0 || xa !== 0) {
            snapBuf[off + F.X] += xv * FIXED_DT + xa * dt2;
            snapBuf[off + F.X_VEL] = xv + xa * FIXED_DT;
          }
          if (yv !== 0 || ya !== 0) {
            snapBuf[off + F.Y] += yv * FIXED_DT + ya * dt2;
            snapBuf[off + F.Y_VEL] = yv + ya * FIXED_DT;
          }
          if (rv !== 0 || ra !== 0) {
            snapBuf[off + F.ROTATE] += rv * FIXED_DT + ra * dt2;
            snapBuf[off + F.ROT_VEL] = rv + ra * FIXED_DT;
          }
          if (wv !== 0 || wa !== 0) {
            snapBuf[off + F.WIDTH] += wv * FIXED_DT + wa * dt2;
            snapBuf[off + F.WIDTH_VEL] = wv + wa * FIXED_DT;
          }
          if (hv !== 0 || ha !== 0) {
            snapBuf[off + F.HEIGHT] += hv * FIXED_DT + ha * dt2;
            snapBuf[off + F.HEIGHT_VEL] = hv + ha * FIXED_DT;
          }
        }

        stepCamera(snapCamera, FIXED_DT);
        snapTime += FIXED_DT;
        applySnap(snapTime);

        if (snapTime >= snapshots.length * SNAPSHOT_INTERVAL) {
          snapshots.push({
            time: snapTime,
            buf: new Float64Array(snapBuf),
            str: snapStr.map((entry) => ({ ...entry })),
            camera: { ...snapCamera },
            numPtr: snapNumPtr,
            strPtr: snapStrPtr,
            camPtr: snapCamPtr,
          });
        }

        if (performance.now() - start > 4) {
          snapTimerRef.current = setTimeout(runChunk, 0);
          return;
        }
      }

      snapshotsRef.current = snapshots;
    };

    snapTimerRef.current = setTimeout(runChunk, 0);
  }, [duration]);

  const rebuildToTime = useCallback(
    (targetTime) => {
      const snapshots = snapshotsRef.current;
      let best = null;
      for (let i = snapshots.length - 1; i >= 0; i--) {
        if (snapshots[i].time <= targetTime) {
          best = snapshots[i];
          break;
        }
      }

      if (best) {
        bufRef.current.set(best.buf);
        const str = strStateRef.current;
        for (let i = 0; i < str.length; i++) {
          Object.assign(str[i], best.str[i]);
        }
        cameraRef.current = { ...best.camera };
        currentTimeRef.current = best.time;
        numTlPtrRef.current = best.numPtr;
        strTlPtrRef.current = best.strPtr;
        camTlPtrRef.current = best.camPtr;
        accumulatorRef.current = 0;
      } else {
        resetState();
        applyNumericTimelines(0);
        applyStringTimelines(0);
        applyCameraTimelines(0);
      }

      while (currentTimeRef.current + FIXED_DT <= targetTime) {
        step(FIXED_DT);
        currentTimeRef.current += FIXED_DT;
        applyNumericTimelines(currentTimeRef.current);
        applyStringTimelines(currentTimeRef.current);
        applyCameraTimelines(currentTimeRef.current);
      }

      const drift = targetTime - currentTimeRef.current;
      if (drift > 0) {
        step(drift);
        currentTimeRef.current = targetTime;
        applyNumericTimelines(targetTime);
        applyStringTimelines(targetTime);
        applyCameraTimelines(targetTime);
      }

      forceRender((value) => value + 1);
    },
    [resetState],
  );

  const pause = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    cancelAnimationFrame(rafRef.current);
    lastTimeRef.current = null;
  }, []);

  const loop = (time) => {
    if (!isPlayingRef.current) return;
    if (!lastTimeRef.current) lastTimeRef.current = time;

    let frameDelta =
      Math.min((time - lastTimeRef.current) / 1000, 0.05) * speedRef.current;
    lastTimeRef.current = time;
    accumulatorRef.current += frameDelta;

    let didStep = false;
    while (accumulatorRef.current >= FIXED_DT) {
      step(FIXED_DT);
      currentTimeRef.current += FIXED_DT;
      applyNumericTimelines(currentTimeRef.current);
      applyStringTimelines(currentTimeRef.current);
      applyCameraTimelines(currentTimeRef.current);
      accumulatorRef.current -= FIXED_DT;
      didStep = true;

      if (duration && currentTimeRef.current >= duration) {
        currentTimeRef.current = duration;
        pause();
        break;
      }
    }

    if (didStep) forceRender((value) => value + 1);
    if (isPlayingRef.current) rafRef.current = requestAnimationFrame(loop);
  };

  const play = useCallback(() => {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;
    setIsPlaying(true);
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const reset = useCallback(() => {
    pause();
    rebuildToTime(0);
  }, [pause, rebuildToTime]);

  const seek = useCallback(
    (time) => {
      pause();
      rebuildToTime(Math.max(0, Math.min(time, duration)));
    },
    [pause, rebuildToTime, duration],
  );

  const setSpeed = useCallback((value) => {
    speedRef.current = value;
  }, []);

  useEffect(() => {
    initEngine(initialComponents, timelines, initialCamera, cameraTimelines);
    rebuildToTime(0);
    buildSnapshots();
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    };
  }, [
    initialComponents,
    timelines,
    initialCamera,
    cameraTimelines,
    initEngine,
    rebuildToTime,
    buildSnapshots,
  ]);

  const getComponents = () => {
    const localComponents = new Array(nRef.current);
    const buf = bufRef.current;
    const str = strStateRef.current;

    for (let i = 0; i < nRef.current; i++) {
      const off = i * FIELDS_PER_COMPONENT;
      const state = str[i];
      localComponents[i] = {
        animationComponentId: state.animationComponentId,
        animationComponentName: state.animationComponentName,
        x: buf[off + F.X],
        y: buf[off + F.Y],
        z: buf[off + F.Z],
        xVelocity: buf[off + F.X_VEL],
        yVelocity: buf[off + F.Y_VEL],
        xAcceleration: buf[off + F.X_ACC],
        yAcceleration: buf[off + F.Y_ACC],
        width: buf[off + F.WIDTH],
        height: buf[off + F.HEIGHT],
        widthVelocity: buf[off + F.WIDTH_VEL],
        widthAcceleration: buf[off + F.WIDTH_ACC],
        heightVelocity: buf[off + F.HEIGHT_VEL],
        heightAcceleration: buf[off + F.HEIGHT_ACC],
        rotate: buf[off + F.ROTATE],
        rotationVelocity: buf[off + F.ROT_VEL],
        rotationAcceleration: buf[off + F.ROT_ACC],
        opacity: buf[off + F.OPACITY],
        originX: buf[off + F.ORIGIN_X],
        originY: buf[off + F.ORIGIN_Y],
        fontSize: buf[off + F.FONT_SIZE],
        fontWeight: buf[off + F.FONT_WEIGHT],
        lineHeight: buf[off + F.LINE_HEIGHT],
        letterSpacing: buf[off + F.LETTER_SPACING],
        shadowBlur: buf[off + F.SHADOW_BLUR],
        shadowOffsetX: buf[off + F.SHADOW_OFFSET_X],
        shadowOffsetY: buf[off + F.SHADOW_OFFSET_Y],
        radiusTopLeft: buf[off + F.RADIUS_TL],
        radiusTopRight: buf[off + F.RADIUS_TR],
        radiusBottomLeft: buf[off + F.RADIUS_BL],
        radiusBottomRight: buf[off + F.RADIUS_BR],
        borderTopWidth: buf[off + F.BORDER_TOP_W],
        borderRightWidth: buf[off + F.BORDER_RIGHT_W],
        borderBottomWidth: buf[off + F.BORDER_BOTTOM_W],
        borderLeftWidth: buf[off + F.BORDER_LEFT_W],
        color: state.color,
        backgroundColor: state.backgroundColor,
        shadowColor: state.shadowColor,
        text: state.text,
        textAlign: state.textAlign,
        verticalAlign: state.verticalAlign,
        overflow: state.overflow,
        shape: state.shape,
        visibility: state.visibility,
        borderTopType: state.borderTopType,
        borderTopColor: state.borderTopColor,
        borderRightType: state.borderRightType,
        borderRightColor: state.borderRightColor,
        borderBottomType: state.borderBottomType,
        borderBottomColor: state.borderBottomColor,
        borderLeftType: state.borderLeftType,
        borderLeftColor: state.borderLeftColor,
      };
    }

    return localComponents;
  };

  return {
    components: getComponents(),
    camera: cameraRef.current,
    play,
    pause,
    reset,
    seek,
    setSpeed,
    isPlaying,
    currentTime: currentTimeRef.current,
    duration,
    rebuildToTime,
  };
};

export default useAnimationEngine;
export { F, FIELDS_PER_COMPONENT, FIELD_NAME_TO_IDX };
